<template>
  <section class="scheduling-board min-w-0 space-y-4">
    <SchedulingVendorTabs v-model="selectedVendorCode" :lanes="lanes">
      <template #actions>
        <PoSearchBox :lanes="lanes" @pick="revealBlock" @clear="selectedPo.clear()" />
        <button type="button" class="btn-secondary" @click="scrollToToday">Focus on current week</button>
        <button v-if="canWrite" type="button" class="btn-primary" @click="openDialog()">Schedule</button>
      </template>
    </SchedulingVendorTabs>

    <CustomerColorLegend />

    <!-- The line-item fetch used to report itself inside the block popup; with that gone it
         reports here. Deliberately ahead of the block below and not inside it: a v-else only
         pairs with the directive on the element immediately before it, so anything slipped
         between store.loading and the grid re-pairs the grid's v-else with it — and the grid
         then unmounts and remounts, losing where it was scrolled to. -->
    <v-alert v-if="lineItems.error.value" type="error" density="compact" variant="tonal" closable @click:close="lineItems.clearError()">
      {{ lineItems.error.value }}
    </v-alert>
    <v-progress-linear v-if="lineItems.loading.value" indeterminate color="primary" />

    <v-alert v-if="store.error" type="error" density="compact" variant="tonal">{{ store.error }}</v-alert>
    <v-progress-linear v-else-if="store.loading" indeterminate color="primary" />

    <ScheduleGrid
      v-else
      ref="grid"
      :rows="boardRows"
      :columns="columns"
      :current-week-key="currentWeekKey"
      :selected-block-key="selectedPo.selectedPoNumber.value"
      @load-earlier="loadEarlierWeeks"
      @load-later="loadLaterWeeks"
    />

    <BlockNoteDialog
      :group="noteBlock?.group ?? null"
      :week-label="noteBlock ? weekLabelFor(noteBlock.weekKey) : ''"
      :can-write="canWrite"
      @save="saveBlockNote"
      @close="noteBlock = null"
    />

    <BlockCapacityDialog
      :group="capacityBlock?.group ?? null"
      :week-label="capacityBlock ? weekLabelFor(capacityBlock.weekKey) : ''"
      :remaining-by-row-id="capacityRemainingByRowId"
      :saving="savingCapacity"
      @save="saveBlockCapacity"
      @close="capacityBlock = null"
    />

    <PoLineItemsDialog :open="lineItems.po.value !== null" :po="lineItems.po.value" @close="lineItems.close()" />

    <CapacityReportDialog
      :open="reportWeekKey !== null"
      :report="capacityReport"
      :week-label="reportWeekKey ? weekLabelFor(reportWeekKey) : ''"
      :vendor-code="selectedLane?.vendorCode ?? ''"
      @close="reportWeekKey = null"
    />

    <ConfirmDialog
      :open="pendingRemoval !== null"
      :title="removeTitle"
      :message="removeMessage"
      confirm-label="Remove"
      @confirm="removePendingBlock"
      @cancel="pendingRemoval = null"
    />

    <ScheduleDialog ref="dialogRef" />
  </section>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import CustomerColorLegend from '@/components/capacity/CustomerColorLegend.vue';
  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import BlockCapacityDialog from '@/components/scheduling/BlockCapacityDialog.vue';
  import BlockNoteDialog from '@/components/scheduling/BlockNoteDialog.vue';
  import CapacityReportDialog from '@/components/scheduling/CapacityReportDialog.vue';
  import PoLineItemsDialog from '@/components/scheduling/PoLineItemsDialog.vue';
  import PoSearchBox from '@/components/scheduling/PoSearchBox.vue';
  import ScheduleDialog from '@/components/scheduling/ScheduleDialog.vue';
  import ScheduleGrid from '@/components/scheduling/ScheduleGrid.vue';
  import { useScheduleGridMetrics } from '@/components/scheduling/scheduleGridMetrics';
  import SchedulingVendorTabs from '@/components/scheduling/SchedulingVendorTabs.vue';
  import { useAccess } from '@/composables/useAccess';
  import { useBoardLineItems } from '@/composables/useBoardLineItems';
  import { useNoPlanMenu } from '@/composables/useNoPlanMenu';
  import { useScheduleBoardColumns } from '@/composables/useScheduleBoardColumns';
  import { useScheduleBoardDragDrop } from '@/composables/useScheduleBoardDragDrop';
  import { useSelectedPo } from '@/composables/useSelectedPo';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { usePoQueueStore } from '@/store/poQueueStore';
  import { PERMISSIONS } from '@/types/access';
  import { generateWeekBuckets, monthsSpanningWeek } from '@/utils/calendarBuckets';
  import { buildCapacityReport } from '@/utils/capacityReport';
  import { eventPathMatches } from '@/utils/eventPath';
  import type { PortionEdit } from '@/utils/blockCapacityEdit';
  import { buildScheduleBoard, groupBlockKey, groupLabel, locateBlock, locateBlockByAssignment } from '@/utils/scheduleBoard';
  import type { BlockSelection, BoardRow, ScheduleGroup, TypeBoardRow, VendorLane } from '@/utils/scheduleBoard';

  // The board opens on last month, this month and next. Weeks either side of that are loaded
  // a month at a time from the buttons the grid shows once you scroll to an edge — most of
  // them are empty, and every extra week is a column the user has to scroll through.
  const BOARD_MONTHS_BEFORE = 1;
  const BOARD_MONTHS_AFTER = 1;
  const BOARD_MONTHS_STEP = 1;
  // Matches CapacityAllocationBar's root class and ScheduleWeekCell's block button class —
  // clicks anywhere inside either must not clear the page-wide PO selection.
  // A card's own actions menu teleports out of the grid, so it has to be named here too —
  // otherwise opening it would clear the selection of the very block it acts on. The PO search
  // sets a selection itself, so clicking back into it (or its list) must not undo that.
  const SEGMENT_OR_BLOCK_SELECTOR = '.allocation-bar, .schedule-card, .schedule-card-menu, .po-search';

  const route = useRoute();
  const store = useCapacityCalendarStore();
  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();
  const { canSeeCustomerMappings, hasPermission } = useAccess();

  const dnd = useScheduleBoardDragDrop();
  const selectedPo = useSelectedPo();
  const lineItems = useBoardLineItems();

  const canWrite = computed(() => hasPermission(PERMISSIONS.WRITE_PO));
  const canWriteCapacity = computed(() => hasPermission(PERMISSIONS.WRITE_CAPACITY));
  const noPlanMenu = useNoPlanMenu();
  const monthsBefore = ref(BOARD_MONTHS_BEFORE);
  const monthsAfter = ref(BOARD_MONTHS_AFTER);
  const buckets = computed(() => generateWeekBuckets(monthsBefore.value, monthsAfter.value));
  const currentWeekKey = computed(() => buckets.value.find((bucket) => bucket.isCurrent)?.key ?? null);

  const grid = ref<InstanceType<typeof ScheduleGrid> | null>(null);
  const dialogRef = ref<InstanceType<typeof ScheduleDialog> | null>(null);
  const selectedVendorCode = ref<string | null>(null);
  // Held only between opening the confirm dialog and answering it — the block a Remove was
  // asked for, since the menu that asked has closed by then.
  const pendingRemoval = ref<BlockSelection | null>(null);
  const reportWeekKey = ref<string | null>(null);
  const noteBlock = ref<BlockSelection | null>(null);
  const capacityBlock = ref<BlockSelection | null>(null);
  const savingCapacity = ref(false);

  const lanes = computed(() => buildScheduleBoard(store.rows, store.allocations));
  const selectedLane = computed(() => lanes.value.find((lane) => lane.vendorCode === selectedVendorCode.value) ?? null);

  const boardRows = computed<BoardRow[]>(() => {
    const lane = selectedLane.value;
    if (!lane) return [];
    const typeRows = store.rows
      .filter((row) => row.vendorId === lane.vendorId && !row.isGhost && row.capacityTypeId)
      .map<TypeBoardRow>((calendarRow) => ({ kind: 'type', lane, calendarRow }));
    return [{ kind: 'scheduled', lane }, ...typeRows];
  });

  // The report names customers, so it follows the same rule as the colour legend: mapping
  // readers only, and gone in privacy mode — otherwise a shared screen with mappings hidden could
  // still open a panel listing every account by name.
  const canOpenReport = computed(() => canSeeCustomerMappings.value && !privacyStore.hideCustomerMappings);
  const canViewActivity = computed(() => hasPermission(PERMISSIONS.READ_PO_ACTIVITY));

  const queueStore = usePoQueueStore();
  const gridMetrics = useScheduleGridMetrics();

  // The queue follows the vendor tab. Hidden or not, it loads: the rail shows the count.
  watch(
    () => selectedLane.value?.vendorId,
    (vendorId) => {
      queueStore.vendorId = vendorId;
    },
    { immediate: true }
  );
  // Narrow screens start with the column collapsed; the flag is still the user's to flip. Not
  // persisted — an auto-hide from a narrow viewport must not stick the next time they open on
  // a wide one.
  watch(
    () => gridMetrics.isNarrow.value,
    (narrow) => {
      if (narrow && !queueStore.hidden) queueStore.setHidden(true, { persist: false });
    },
    { immediate: true }
  );
  watch(
    () => queueStore.hidden,
    (hidden) => gridMetrics.setQueueHidden(hidden),
    { immediate: true }
  );

  function scheduleFromQueue(poNumber: string): void {
    const vendorId = selectedLane.value?.vendorId;
    if (!vendorId) return;
    openDialog({ vendorId, poNumber });
  }

  const columns = useScheduleBoardColumns({
    buckets,
    selectedLane,
    canWrite,
    canWriteCapacity,
    canViewActivity,
    canOpenReport,
    selectedPo,
    noPlanMenu,
    onSelectGroup: selectBlock,
    onViewNote: (lane, weekKey, group) => {
      noteBlock.value = { lane, weekKey, group };
    },
    onEditCapacity: (lane, weekKey, group) => {
      capacityBlock.value = { lane, weekKey, group };
    },
    onViewLineItems: (lane, group) => void openLineItems(lane, group),
    onRemove: askToRemoveBlock,
    onDragStart: startBlockDrag,
    onDrop: (vendorCode, weekKey) => void moveBlockTo(vendorCode, weekKey),
    onScheduleWeek: openDialogForWeek,
    onOpenReport: (weekKey) => {
      reportWeekKey.value = weekKey;
    },
    queue: {
      items: computed(() => queueStore.items),
      loading: computed(() => queueStore.loading),
      initialLoading: computed(() => queueStore.initialLoading),
      error: computed(() => queueStore.error),
      syncedAt: computed(() => queueStore.syncedAt),
      syncError: computed(() => queueStore.syncError),
      refreshError: computed(() => queueStore.refreshError),
      hidden: computed(() => queueStore.hidden),
    },
    onToggleQueue: () => queueStore.setHidden(!queueStore.hidden),
    onRefreshQueue: () => void queueStore.refresh(),
    onScheduleFromQueue: scheduleFromQueue,
    onQueueDragStart: startQueueDrag,
  });

  const capacityReport = computed(() => {
    const lane = selectedLane.value;
    const weekKey = reportWeekKey.value;
    if (!lane || !weekKey) return null;
    const vendorRows = store.rows.filter((row) => row.vendorId === lane.vendorId && !row.isGhost && row.capacityTypeId);
    return buildCapacityReport(vendorRows, store.allocations, weekKey, mappingStore.items);
  });

  const capacityRemainingByRowId = computed<Record<string, number>>(() => {
    const pending = capacityBlock.value;
    if (!pending) return {};
    return pending.group.portions.reduce<Record<string, number>>((remaining, portion) => {
      const allocation = store.allocations[portion.rowId]?.[pending.weekKey];
      return allocation ? { ...remaining, [portion.rowId]: allocation.remainedCapacity } : remaining;
    }, {});
  });

  const removeTitle = computed(() => (pendingRemoval.value ? `Remove ${groupLabel(pendingRemoval.value.group)}?` : ''));

  const removeMessage = computed(() => {
    const pending = pendingRemoval.value;
    if (!pending) return '';
    const typeCodes = pending.group.portions.map((portion) => portion.typeCode).join(', ');
    return `This removes the whole block — every linked piece goes with it:\n${typeCodes}`;
  });

  function weekLabelFor(weekKey: string): string {
    return buckets.value.find((bucket) => bucket.key === weekKey)?.label ?? weekKey;
  }

  // Clicking a card only picks the block out across the capacity rows now; everything it
  // used to open a panel for lives in the card's own menu.
  function selectBlock(weekKey: string, group: ScheduleGroup): void {
    lineItems.clearError();
    selectedPo.select(groupBlockKey(group), { blockKey: group.key, weekKey });
  }

  function startBlockDrag(lane: VendorLane, weekKey: string, group: ScheduleGroup): void {
    dnd.startDrag({ group, vendorCode: lane.vendorCode, fromWeekKey: weekKey });
  }

  function startQueueDrag(poNumber: string): void {
    const vendorCode = selectedLane.value?.vendorCode;
    if (!vendorCode) return;
    dnd.startQueueDrag({ poNumber, vendorCode });
  }

  async function moveBlockTo(vendorCode: string, weekKey: string): Promise<void> {
    const resolution = dnd.completeDrop({ vendorCode, weekKey });
    if (!resolution) return;

    if (resolution.kind === 'queue') {
      const vendorId = selectedLane.value?.vendorId;
      if (vendorId) openDialog({ vendorId, weekKey: resolution.targetWeekKey, poNumber: resolution.poNumber });
      return;
    }

    const [portion] = resolution.group.portions;
    if (resolution.kind === 'group') {
      await store.moveDistributionGroup(portion.rowId, resolution.group.key, resolution.targetWeekKey);
    } else {
      await store.moveAssignment(portion.rowId, resolution.fromWeekKey, portion.assignmentId, resolution.targetWeekKey);
    }
  }

  function askToRemoveBlock(lane: VendorLane, weekKey: string, group: ScheduleGroup): void {
    pendingRemoval.value = { lane, weekKey, group };
  }

  async function removePendingBlock(): Promise<void> {
    const pending = pendingRemoval.value;
    pendingRemoval.value = null;
    if (!pending) return;
    const [portion] = pending.group.portions;
    const ok = await store.removePoAssignment(portion.rowId, pending.weekKey, portion.assignmentId);
    if (ok) {
      selectedPo.clear();
    }
  }

  // The note is written through the block's first portion; the backend cascades it across
  // the rest of a distributed block's rows.
  async function saveBlockNote(notes: string): Promise<void> {
    const pending = noteBlock.value;
    if (!pending) return;
    const [portion] = pending.group.portions;
    const ok = await store.updatePoAssignmentNotes(portion.rowId, pending.weekKey, portion.assignmentId, notes);
    if (ok) {
      noteBlock.value = null;
    }
  }

  // Each capacity type is its own assignment row and its own PATCH, so a distributed block
  // saves as one call per changed portion. A failure part-way leaves the earlier ones written
  // and surfaces its own toast — the store reports per call.
  async function saveBlockCapacity(edits: PortionEdit[]): Promise<void> {
    const pending = capacityBlock.value;
    if (!pending || edits.length === 0) return;
    savingCapacity.value = true;
    try {
      const results = await edits.reduce<Promise<boolean[]>>(
        async (previous, { portion, quantity }) => [
          ...(await previous),
          await store.updatePoAssignmentQuantity(portion.rowId, pending.weekKey, portion.assignmentId, quantity),
        ],
        Promise.resolve([]),
      );
      if (results.every(Boolean)) {
        capacityBlock.value = null;
      }
    } finally {
      savingCapacity.value = false;
    }
  }

  async function openLineItems(lane: VendorLane, group: ScheduleGroup): Promise<void> {
    if (!group.poNumber || !lane.vendorId) return;
    await lineItems.open(group.poNumber, lane.vendorId);
  }

  function openDialog(prefill?: { vendorId?: string; weekKey?: string; poNumber?: string }): void {
    dialogRef.value?.openDialog(prefill);
  }

  function openDialogForWeek(weekKey: string): void {
    const vendorId = selectedLane.value?.vendorId;
    if (!vendorId) return;
    openDialog({ vendorId, weekKey });
  }

  function isOverlayOpen(): boolean {
    return (
      pendingRemoval.value !== null ||
      noteBlock.value !== null ||
      capacityBlock.value !== null ||
      lineItems.po.value !== null ||
      reportWeekKey.value !== null
    );
  }

  // Prepended weeks push every existing column right, so the grid scrolls by the same amount
  // to stay put. Appending needs no such correction, which is why only this one measures.
  async function loadEarlierWeeks(): Promise<void> {
    const weeksBefore = buckets.value.length;
    monthsBefore.value += BOARD_MONTHS_STEP;
    const added = buckets.value.length - weeksBefore;
    await nextTick();
    grid.value?.scrollByColumns(added);
  }

  function loadLaterWeeks(): void {
    monthsAfter.value += BOARD_MONTHS_STEP;
  }

  async function scrollToToday(): Promise<void> {
    await nextTick();
    const weekKey = currentWeekKey.value;
    if (weekKey) {
      await grid.value?.scrollToWeek(weekKey);
    }
  }

  function queryParam(name: string): string | null {
    const value = route.query[name];
    return (Array.isArray(value) ? value[0] : value) || null;
  }

  // Open on the vendor tab that holds the block, select it and land on its week — widening
  // the loaded range first when the week is outside it, since the grid can only scroll to a
  // column it has. Shared by the search box and the activity-log deep link.
  async function revealBlock(found: BlockSelection): Promise<void> {
    selectedVendorCode.value = found.lane.vendorCode;
    const span = monthsSpanningWeek(found.weekKey);
    monthsBefore.value = Math.max(monthsBefore.value, span.before);
    monthsAfter.value = Math.max(monthsAfter.value, span.after);
    // The tab watcher clears the selection on the next flush; select after it has run.
    await nextTick();
    selectedPo.select(groupBlockKey(found.group), { blockKey: found.group.key, weekKey: found.weekKey });
    await grid.value?.scrollToWeek(found.weekKey);
  }

  // Deep link from the activity log: ?assignmentId= names the exact block the latest change
  // touched; ?po= is the fallback when that block is gone (its family's earliest card). Read
  // once on load — the selection is the board's own state from then on, so Escape clears it
  // without fighting the URL.
  async function openLinkedBlock(): Promise<boolean> {
    const assignmentId = queryParam('assignmentId');
    const poNumber = queryParam('po');
    const found = (assignmentId && locateBlockByAssignment(lanes.value, assignmentId)) || (poNumber && locateBlock(lanes.value, poNumber)) || null;
    if (!found) return false;
    await revealBlock(found);
    return true;
  }

  // Escape clears the block selection, wherever it was made — a card or a bar segment.
  function onBoardKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && selectedPo.selectedPoNumber.value) {
      selectedPo.clear();
    }
  }

  // The same for a click: outside any allocation bar/segment or scheduled block, clear it.
  // Segments and blocks are exempted — their own click handlers own toggle/select, and
  // mousedown fires before click, so clearing here first would race and re-select.
  // Checked on the event's path, not by walking up from the target: a search-result row has
  // selected its block and closed its list by the time the event gets here, so the row is
  // already out of the document and closest() would find nothing above it.
  function onBoardDocumentMouseDown(event: MouseEvent): void {
    if (isOverlayOpen() || !selectedPo.selectedPoNumber.value) return;
    if (eventPathMatches(event, SEGMENT_OR_BLOCK_SELECTOR)) return;
    selectedPo.clear();
  }

  watch(
    lanes,
    (value) => {
      if (!selectedVendorCode.value && value.length > 0) {
        selectedVendorCode.value = value[0].vendorCode;
      }
    },
    { immediate: true },
  );

  // Stale PO selection from another vendor's rows shouldn't linger after a tab switch.
  watch(selectedVendorCode, () => selectedPo.clear());

  onMounted(async () => {
    document.addEventListener('keydown', onBoardKeydown);
    document.addEventListener('mousedown', onBoardDocumentMouseDown);

    const loads: Promise<void>[] = [store.load()];
    if (!mappingStore.loaded) {
      loads.push(mappingStore.load());
    }
    await Promise.all(loads);
    if (!(await openLinkedBlock())) {
      await scrollToToday();
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onBoardKeydown);
    document.removeEventListener('mousedown', onBoardDocumentMouseDown);
  });
</script>

<style scoped>
  .scheduling-board {
    width: 100%;
    max-width: 100%;
  }
</style>
